import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Table from '../ogComponent/table';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import "./attendence.css";

export default function WorkingHours() {
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const userId = localStorage.getItem('userId');

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const navigate = useNavigate();
    const token = localStorage.getItem('accessToken');

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleDateChange = (newDate, type) => {
        if (type === 'start') {
            setStartDate(newDate);
        } else if (type === 'end') {
            setEndDate(newDate);
        }
    };

    const fetchWorkingHours = async () => {
        setLoading(true);
        setError(null);

        try {
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/employee-worked-hours-summary/`,
                {
                    params: {
                        start_date: startDate.format('YYYY-MM-DD'),
                        end_date: endDate.format('YYYY-MM-DD'),
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!Array.isArray(response.data)) {
                throw new Error('Invalid data format received from server');
            }

            const formattedData = response.data.map((item, index) => ({
                si_no: index + 1,
                employee_code: item.employee_code || 'N/A',
                id: item.id,
                name: (
                    <div className="d-flex align-items-center gap-3">
                        <img
                            src={item.profile_photo || '/assets/user.png'}
                            className="att-cell-img"
                            alt="user"
                        />
                        <span className="att-name-text">{item.name || 'N/A'}</span>
                    </div>
                ),
                branch: item.branch?.branch_name || 'N/A',
                position: item.job_position?.job_title || 'N/A',
                working_hours: (
                    <span className="fw-bold text-main" style={{ fontSize: '15px' }}>
                        {item.total_worked_hours || '0'}
                    </span>
                ),
            }));

            setData(formattedData);
        } catch (error) {
            console.error('Error fetching working hours data:', error);
            setError(error.message || 'Failed to fetch working hours data');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions.includes("showworkinghoursID")) {
            fetchWorkingHours();
        } else {
            setLoading(false);
        }
    }, [startDate, endDate, permissions]);

    const handleUser = (item) => {
        if (item && item.id && (permissions.includes("showindividualworkinghoursID") || String(item.id) === String(userId))) {
            navigate(`/user/workinghours/${item.id}`);
        }
    };

    const header = [
        { name: 'si_no', label: '#', active: true },
        { name: 'employee_code', label: 'Emp ID', active: true },
        { name: 'name', label: 'Employee Name', active: true },
        { name: 'branch', label: 'Branch', active: true },
        { name: 'position', label: 'Job Title', active: true },
        { name: 'working_hours', label: 'Total Hours', active: true },
    ];

    return (
        <div className="AttendanceContainer w-100 h-100 px-4 pt-4 pb-5">
            <div className="attendance-header mb-4">
                <h3 className="m-0">Work Duration Summary</h3>
                <p className="text-muted small mb-0">Aggregate total hours worked across specific date ranges and branches.</p>
            </div>

            <div className="attendance-filters">
                <div className="fw-bold text-main">Filter Range</div>
                <div className="date-picker-group">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From"
                            value={startDate}
                            onChange={(newValue) => handleDateChange(newValue, 'start')}
                            maxDate={endDate}
                            slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                        />
                        <DatePicker
                            label="To"
                            value={endDate}
                            onChange={(newValue) => handleDateChange(newValue, 'end')}
                            minDate={startDate}
                            slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                        />
                    </LocalizationProvider>
                </div>
            </div>

            {error ? (
                <div className="alert alert-danger shadow-sm border-0 rounded-4 p-4 d-flex align-items-center gap-3">
                    <div className="bg-white rounded-circle p-2 shadow-sm text-danger"><i className="bi bi-exclamation-triangle-fill"></i></div>
                    <div>
                        <h6 className="mb-0 fw-bold">Fetch Error</h6>
                        <small>{error}</small>
                    </div>
                </div>
            ) : (
                <div className="attendance-table-wrapper">
                    {permissions.includes("showworkinghoursID") ? (
                        <Table
                            header={header}
                            product={data}
                            onRowClick={handleUser}
                            loading={loading}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    ) : (
                        <div className="alert alert-warning border-0 rounded-4">No permission to view working hours.</div>
                    )}
                </div>
            )}
        </div>
    );
}